import React, { useState } from 'react';
import { ModuleContent } from '../types';
import { 
  CheckCircle, XCircle, Bot, Clock, MessageCircle, Shield, Eye, Activity, 
  Lightbulb, AlertTriangle, ArrowRight, FileWarning, Download, MailWarning, 
  PackageOpen, ShieldAlert, Wifi 
} from 'lucide-react';
import { askTutor, getOfflineTip } from '../services/geminiService';

interface ModuleDetailProps {
  module: ModuleContent;
  onComplete: (id: string, score: number) => void;
  isCompleted: boolean;
  classModeEnabled: boolean;
}

export const ModuleDetail: React.FC<ModuleDetailProps> = ({ module, onComplete, isCompleted, classModeEnabled }) => {
  const [activeTab, setActiveTab] = useState<'learn' | 'quiz'>('learn');
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);
  
  // Gemini State
  const [tutorQuestion, setTutorQuestion] = useState('');
  const [tutorAnswer, setTutorAnswer] = useState('');
  const [isThinking, setIsThinking] = useState(false);

  const handleQuizSubmit = () => {
    let correct = 0;
    module.quiz.forEach(q => {
      if (answers[q.id] === q.correctIndex) correct++;
    });
    const score = (correct / module.quiz.length) * 100;
    onComplete(module.id, score);
    setShowResults(true);
  };

  const handleAskTutor = async () => {
    if (!tutorQuestion.trim()) return;
    setIsThinking(true);
    setTutorAnswer(''); // Clear previous answer
    
    // Context includes new standardized fields
    const context = `Module: ${module.title}. Definition: ${module.definition}. Objectives: ${module.objectives}. Prevention: ${module.prevention.join(', ')}.`;
    
    const result = await askTutor({
      prompt: tutorQuestion, 
      context, 
      moduleId: module.id
    });
    
    if (result.ok && result.reply) {
      setTutorAnswer(result.reply);
    } else {
      const tip = getOfflineTip(module.id);
      setTutorAnswer(`${result.error}\n\n${tip}`);
    }
    
    setIsThinking(false);
  };

  const setSuggestedQuestion = (q: string) => {
    setTutorQuestion(q);
  };

  const getEntryIcon = (id: string) => {
    switch (id) {
      case 'virus-worm': return <FileWarning size={20} />;
      case 'trojan': return <Download size={20} />;
      case 'ransomware': return <MailWarning size={20} />;
      case 'spyware-keyloggers': return <PackageOpen size={20} />;
      case 'rootkits': return <ShieldAlert size={20} />;
      case 'botnets': return <Wifi size={20} />;
      default: return <ArrowRight size={20} />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      
      {/* Class Mode Banner */}
      {classModeEnabled && module.classMode && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex flex-col md:flex-row gap-6 animate-fade-in shadow-sm">
          <div className="flex items-center gap-3 border-r-0 md:border-r border-yellow-200 pr-0 md:pr-6 min-w-max">
            <div className="bg-yellow-100 p-2 rounded-full">
              <Clock className="text-yellow-700" size={24} />
            </div>
            <div>
              <p className="text-xs font-bold text-yellow-600 uppercase">Suggested Time</p>
              <p className="text-lg font-bold text-yellow-900">{module.classMode.duration}</p>
            </div>
          </div>
          
          <div className="flex-grow">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle className="text-yellow-700" size={18} />
              <span className="text-xs font-bold text-yellow-600 uppercase">Discussion Prompt</span>
            </div>
            <ul className="list-disc pl-5 space-y-1">
              {module.classMode.discussion.map((prompt, i) => (
                <li key={i} className="text-yellow-900 text-sm font-medium italic">"{prompt}"</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-cyber-dark text-white p-6 flex justify-between items-center">
          <h2 className="text-3xl font-bold">{module.title}</h2>
          {isCompleted && <span className="bg-green-500 text-xs font-bold px-3 py-1 rounded-full uppercase">Completed</span>}
        </div>

        <div className="flex border-b">
          <button 
            onClick={() => setActiveTab('learn')} 
            className={`flex-1 p-4 font-bold text-lg ${activeTab === 'learn' ? 'text-cyber-accent border-b-4 border-cyber-accent' : 'text-slate-500'}`}
          >
            Learn
          </button>
          <button 
            onClick={() => setActiveTab('quiz')} 
            className={`flex-1 p-4 font-bold text-lg ${activeTab === 'quiz' ? 'text-cyber-accent border-b-4 border-cyber-accent' : 'text-slate-500'}`}
          >
            Check Understanding
          </button>
        </div>

        <div className="p-6 md:p-8">
          {activeTab === 'learn' ? (
            <div className="space-y-8 animate-fade-in">
              {/* Introduction Section */}
              <section>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg hidden md:block">
                     <Lightbulb className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-cyber-dark mb-2">What is it?</h3>
                    <p className="text-lg leading-relaxed text-slate-700">{module.definition}</p>
                  </div>
                </div>
              </section>
              
              <div className="grid md:grid-cols-2 gap-6">
                <section className="bg-slate-50 p-4 rounded-lg border-l-4 border-orange-400">
                  <h3 className="text-lg font-bold mb-2 text-orange-700 flex items-center gap-2">
                    {getEntryIcon(module.id)} How it gets in
                  </h3>
                  <p className="text-slate-700">{module.entryVectors}</p>
                </section>
                <section className="bg-slate-50 p-4 rounded-lg border-l-4 border-red-500">
                  <h3 className="text-lg font-bold mb-2 text-red-700 flex items-center gap-2">
                    <AlertTriangle size={18} /> Goal
                  </h3>
                  <p className="text-slate-700">{module.objectives}</p>
                </section>
              </div>

              {/* Warning Signs */}
              <section className="bg-yellow-50 p-5 rounded-xl border border-yellow-100">
                 <h3 className="text-lg font-bold text-yellow-800 mb-3 flex items-center gap-2">
                   <AlertTriangle className="text-yellow-600" size={20} /> Early Warning Signs
                 </h3>
                 <div className="grid md:grid-cols-3 gap-4">
                   {module.warningSigns.map((sign, i) => (
                     <div key={i} className="bg-white p-3 rounded shadow-sm text-sm border-l-2 border-yellow-400 text-slate-700 font-medium">
                       {sign}
                     </div>
                   ))}
                 </div>
              </section>

              {/* PDR Strategy Section */}
              <section>
                <h3 className="text-2xl font-bold text-cyber-dark mb-4 text-center">Defensive Strategy</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Prevent */}
                  <div className="border rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-green-100 p-3 flex items-center gap-2 border-b border-green-200">
                      <Shield className="text-green-700" size={20} />
                      <h4 className="font-bold text-green-900">Prevent</h4>
                    </div>
                    <ul className="p-4 space-y-2">
                      {module.prevention.map((item, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-green-500 font-bold">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Detect */}
                  <div className="border rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-blue-100 p-3 flex items-center gap-2 border-b border-blue-200">
                      <Eye className="text-blue-700" size={20} />
                      <h4 className="font-bold text-blue-900">Detect</h4>
                    </div>
                    <ul className="p-4 space-y-2">
                      {module.detection.map((item, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-blue-500 font-bold">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Respond */}
                  <div className="border rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-red-100 p-3 flex items-center gap-2 border-b border-red-200">
                      <Activity className="text-red-700" size={20} />
                      <h4 className="font-bold text-red-900">Respond</h4>
                    </div>
                    <ul className="p-4 space-y-2">
                      {module.response.map((item, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-red-500 font-bold">•</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              {/* Myth vs Reality */}
              <section className="bg-slate-800 text-white p-6 rounded-xl">
                <div className="flex flex-col md:flex-row gap-6 items-center">
                   <div className="flex-1 text-center md:text-right">
                     <h4 className="text-red-400 font-bold uppercase tracking-wider text-xs mb-1">Common Myth</h4>
                     <p className="font-serif italic text-lg opacity-80">"{module.myth.myth}"</p>
                   </div>
                   <div className="h-px w-full md:w-px md:h-16 bg-slate-600"></div>
                   <div className="flex-1 text-center md:text-left">
                     <h4 className="text-green-400 font-bold uppercase tracking-wider text-xs mb-1">Reality</h4>
                     <p className="font-medium">{module.myth.reality}</p>
                   </div>
                </div>
              </section>

              {/* Ask the Tutor Panel */}
              <section className="mt-10 pt-6 border-t-2 border-indigo-100">
                <div className="bg-gradient-to-br from-indigo-50 to-white p-6 rounded-xl border border-indigo-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-indigo-600 p-2 rounded-full shadow-lg">
                      <Bot className="text-white w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-indigo-900 text-xl">Ask the Tutor</h3>
                      <p className="text-sm text-indigo-600">AI-Powered revisions & examples</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-xs font-bold text-slate-500 uppercase mb-2">Suggested Questions:</p>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setSuggestedQuestion("Give me a real world analogy.")} className="text-xs bg-white border border-indigo-200 text-indigo-700 px-3 py-1 rounded-full hover:bg-indigo-50 transition-colors">
                        "Give me an analogy"
                      </button>
                      <button onClick={() => setSuggestedQuestion("How do I best defend against this?")} className="text-xs bg-white border border-indigo-200 text-indigo-700 px-3 py-1 rounded-full hover:bg-indigo-50 transition-colors">
                        "How do I defend?"
                      </button>
                      <button onClick={() => setSuggestedQuestion("Summarise the key points.")} className="text-xs bg-white border border-indigo-200 text-indigo-700 px-3 py-1 rounded-full hover:bg-indigo-50 transition-colors">
                        "Summarise this"
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={tutorQuestion}
                      onChange={(e) => setTutorQuestion(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAskTutor()}
                      placeholder={`Type a question about ${module.title}...`}
                      className="flex-1 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none shadow-inner"
                    />
                    <button 
                      onClick={handleAskTutor}
                      disabled={isThinking || !tutorQuestion.trim()}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
                    >
                      {isThinking ? '...' : 'Ask'}
                    </button>
                  </div>
                  
                  {tutorAnswer && (
                    <div className="mt-4 animate-fade-in p-4 bg-white rounded-lg border-l-4 border-indigo-500 shadow-sm">
                      <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">{tutorAnswer}</p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          ) : (
            <div className="space-y-6">
              {module.quiz.map((q, idx) => (
                <div key={q.id} className="border-b pb-6 last:border-0">
                  <p className="font-bold text-lg mb-4">{idx + 1}. {q.text}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oIdx) => (
                      <button
                        key={oIdx}
                        onClick={() => !showResults && setAnswers({...answers, [q.id]: oIdx})}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          showResults 
                            ? oIdx === q.correctIndex 
                              ? 'bg-green-100 border-green-500' 
                              : answers[q.id] === oIdx 
                                ? 'bg-red-100 border-red-500' 
                                : 'bg-gray-50'
                            : answers[q.id] === oIdx 
                              ? 'bg-blue-100 border-blue-500 shadow-md' 
                              : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span>{opt}</span>
                          {showResults && oIdx === q.correctIndex && <CheckCircle className="text-green-600" size={20}/>}
                          {showResults && answers[q.id] === oIdx && oIdx !== q.correctIndex && <XCircle className="text-red-600" size={20}/>}
                        </div>
                      </button>
                    ))}
                  </div>
                  {showResults && (
                    <div className="mt-3 p-3 bg-blue-50 text-blue-800 rounded text-sm">
                      <strong>Explanation:</strong> {q.explanation}
                    </div>
                  )}
                </div>
              ))}
              
              {!showResults ? (
                <button 
                  onClick={handleQuizSubmit}
                  disabled={Object.keys(answers).length < module.quiz.length}
                  className="w-full bg-cyber-accent text-white py-3 rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
                >
                  Check Answers
                </button>
              ) : (
                 <div className="text-center p-4 bg-green-50 rounded-lg">
                   <p className="font-bold text-green-800 mb-2">Module Completed!</p>
                   <button onClick={() => setActiveTab('learn')} className="text-green-700 underline">Review Content</button>
                 </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
